import React from "react";
import {
  Box,
  Flex,
  Link,
  Button,
  Stack,
  Icon,
  Slide,
  useColorMode,
  useColorModeValue,
  Text,
  Divider,
} from "@chakra-ui/react";
import {
  FaHome,
  FaSun,
  FaMoon,
  FaTimes,
  FaRupeeSign,
  FaBlog,
  FaRegFileAlt,
  FaHandsHelping,
  FaRocket,
  FaQuestionCircle,
  FaInfoCircle,
  FaCogs,
  FaLock,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";

const EXPLORE_LINKS = [
  { label: "Pricing", to: "/pricing", icon: FaRupeeSign },
  { label: "Product tour", to: { pathname: "/", hash: "features" }, icon: FaRocket },
  { label: "Blog", to: "/blog", icon: FaBlog },
  { label: "Template gallery", to: "/templates", icon: FaRegFileAlt },
  { label: "Success stories", to: "/success-stories", icon: FaHandsHelping },
];

function DrawerSectionTitle({ children }) {
  const c = useColorModeValue("teal.700", "teal.300");
  return (
    <Text fontSize="10px" fontWeight="extrabold" color={c} letterSpacing="0.22em" textTransform="uppercase" mb={3}>
      {children}
    </Text>
  );
}

/** Slide-out navigation only — open trigger lives in Header. */
const Navbar = ({ isOpen, onClose }) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const bg = useColorModeValue("rgba(255, 255, 255, 0.94)", "rgba(15, 23, 42, 0.92)");
  const color = useColorModeValue("blue.600", "blue.200");
  const overlayBg = useColorModeValue("blackAlpha.450", "blackAlpha.760");
  const sectionBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const sectionBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  const linkHoverBg = useColorModeValue("teal.50", "whiteAlpha.100");

  return (
    <>
      {isOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100%"
          bg={overlayBg}
          zIndex={1000}
          onClick={onClose}
          backdropFilter="blur(4px)"
        />
      )}
      <Slide direction="right" in={isOpen} style={{ zIndex: 1001 }}>
        <Box
          pos="fixed"
          top={0}
          right={0}
          h="100vh"
          w={{ base: "100%", sm: "360px", md: "380px" }}
          bg={bg}
          boxShadow="2xl"
          backdropFilter="blur(16px)"
          WebkitBackdropFilter="blur(16px)"
          py={5}
          px={5}
          overflowY="auto"
          borderLeftWidth={{ base: 0, sm: "1px" }}
          borderColor={useColorModeValue("gray.200", "gray.600")}
        >
          <Stack spacing={8}>
            <Flex alignItems="center" justify="space-between">
              <Link as={RouterLink} to="/" onClick={onClose}>
                <Button
                  variant="ghost"
                  color={color}
                  px={2}
                  leftIcon={<FaHome />}
                  aria-label="Home"
                  fontWeight="bold"
                  _hover={{ bg: linkHoverBg }}
                >
                  Home
                </Button>
              </Link>
              <Flex gap={2}>
                <Button onClick={toggleColorMode} variant="ghost" size="sm" colorScheme="teal" aria-label="Toggle theme">
                  {colorMode === "light" ? <FaMoon /> : <FaSun />}
                </Button>
                <Button onClick={onClose} variant="ghost" size="sm" colorScheme="gray" aria-label="Close menu">
                  <Icon as={FaTimes} boxSize={5} />
                </Button>
              </Flex>
            </Flex>

            <Divider />

            {/* Explore */}
            <Box bg={sectionBg} borderRadius="xl" borderWidth="1px" borderColor={sectionBorder} p={4}>
              <DrawerSectionTitle>Explore</DrawerSectionTitle>
              <Stack spacing={0}>
                {EXPLORE_LINKS.map((item) => (
                  <Link
                    key={item.label}
                    as={RouterLink}
                    to={item.to}
                    onClick={onClose}
                    display="flex"
                    alignItems="center"
                    py={2.5}
                    px={2}
                    mx={-2}
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight="semibold"
                    _hover={{ textDecoration: "none", bg: linkHoverBg, color: color }}
                  >
                    <Icon as={item.icon} boxSize={4} mr={3} opacity={0.9} />
                    {item.label}
                  </Link>
                ))}
              </Stack>
            </Box>

            {/* Help & legal */}
            <Box bg={sectionBg} borderRadius="xl" borderWidth="1px" borderColor={sectionBorder} p={4}>
              <DrawerSectionTitle>Help & legal</DrawerSectionTitle>
              <Stack spacing={0}>
                {[
                  { to: "/contact-us", label: "Contact", icon: FaQuestionCircle },
                  { to: "/faqs", label: "FAQs", icon: FaInfoCircle },
                  { to: "/terms-and-conditions", label: "Terms", icon: FaCogs },
                  { to: "/privacy-policy", label: "Privacy", icon: FaLock },
                ].map((row) => (
                  <Link
                    key={row.to}
                    as={RouterLink}
                    to={row.to}
                    onClick={onClose}
                    display="flex"
                    alignItems="center"
                    py={2.5}
                    px={2}
                    mx={-2}
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight="medium"
                    _hover={{ textDecoration: "none", bg: linkHoverBg, color: color }}
                  >
                    <Icon as={row.icon} boxSize={4} mr={3} opacity={0.85} />
                    {row.label}
                  </Link>
                ))}
              </Stack>
            </Box>

          </Stack>
        </Box>
      </Slide>
    </>
  );
};

export default Navbar;
